// sticky.ts
import type { Context } from '../types/context';
import { isMobile } from '../utils';
import {
  initializeStickyHorizontal,
  initializeStickyMobile,
  initializeStickyVertical,
  type StickyOverrideConfig,
} from './sticky-dynamic';

// Extended type to support nested configurations for style-expand
type ExtendedStickyOverrideConfig = StickyOverrideConfig & {
  toolbar?: StickyOverrideConfig;
  panel?: StickyOverrideConfig;
  body?: StickyOverrideConfig;
};

/** Small query helpers */
const qs = <T extends Element = HTMLElement>(sel: string, root: Document | Element = document) =>
  root.querySelector<T>(sel);

export const getFilterSettings = (context) => {
  if (!context) {
    console.error('context getFilterSettings null');
    return {};
  }

  // swatch_settings get from filterSettings app admin
  return {
    ...(context?.templateSettings?.filterSettings || {}),
    swatch_settings: context?.app?.filterSettings?.swatch_settings,
  };
};

export const blockFilterOpening = (context) => {
  const {
    stickyFilterOnMobile,
    filterTreeMobileStyle,
    stickyFilterOnDesktop,
    filterLayout,
    filterTreeHorizontalStyle,
  } = getFilterSettings(context);
  if (!stickyFilterOnMobile && !stickyFilterOnDesktop) {
    return;
  }

  const isMobileSize = isMobile(context?.app?.generalSettings?.isTabletPortraitMax);
  const toggleButtonSelector = isMobileSize
    ? '.boost-sd__toolbar-top-mobile .boost-sd__filter-tree-toggle-button'
    : '.boost-sd__filter-tree-toggle-button--expand';

  const shouldApplyMobile = () => {
    return stickyFilterOnMobile && filterTreeMobileStyle === 'style1';
  };

  const shouldApplyDesktop = () => {
    return (
      stickyFilterOnDesktop &&
      filterLayout === 'horizontal' &&
      filterTreeHorizontalStyle === 'style-expand'
    );
  };

  const performToggleAction = (button: HTMLElement) => {
    if (shouldApplyDesktop() || shouldApplyMobile()) {
      if (button.classList.contains('boost-sd__filter-tree-toggle-button--active')) {
        document.body.classList.add('boost-sd__sticky-opening');
      } else {
        document.body.classList.remove('boost-sd__sticky-opening');
      }
    }
  };

  const checkAndPerformAction = () => {
    const filterTreeToggleButton = qs(toggleButtonSelector);
    if (filterTreeToggleButton) {
      performToggleAction(filterTreeToggleButton);
    }
  };

  // Initial action
  checkAndPerformAction();

  // Set up observer for the entire document to handle dynamic DOM changes
  if (shouldApplyDesktop() || shouldApplyMobile()) {
    // Clean up existing observer if any
    const windowWithObserver = window as Window & { _filterOpeningObserver?: MutationObserver };
    if (windowWithObserver._filterOpeningObserver) {
      windowWithObserver._filterOpeningObserver.disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      let shouldCheck = false;

      mutations.forEach((mutation) => {
        // Check for added nodes that might contain our toggle button
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          for (const node of Array.from(mutation.addedNodes)) {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;
              // Check if the added node is our button or contains our button
              if (
                element.matches?.(toggleButtonSelector) ||
                element.querySelector?.(toggleButtonSelector)
              ) {
                shouldCheck = true;
                break;
              }
            }
          }
        }

        // Check for class changes on our target button
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target instanceof HTMLElement &&
          mutation.target.matches(toggleButtonSelector)
        ) {
          performToggleAction(mutation.target);
        }
      });

      // If new nodes were added that might affect our button, recheck
      if (shouldCheck) {
        checkAndPerformAction();
      }
    });

    // Observe both attribute changes and child list changes
    observer.observe(document.body, {
      attributes: true,
      childList: true,
      subtree: true,
      attributeFilter: ['class'],
    });

    // Store observer on window for cleanup
    windowWithObserver._filterOpeningObserver = observer;
  }
};

export const cleanupFilterOpeningObserver = () => {
  const windowWithObserver = window as Window & { _filterOpeningObserver?: MutationObserver };

  if (windowWithObserver._filterOpeningObserver) {
    windowWithObserver._filterOpeningObserver.disconnect();
    delete windowWithObserver._filterOpeningObserver;
  }
};

export const getStickyStatus = (context: Context) => {
  const { stickyFilterOnMobile, stickyFilterOnDesktop } = getFilterSettings(context);
  return {
    stickyEnabled: stickyFilterOnDesktop || stickyFilterOnMobile,
    stickyFilterOnMobile,
    stickyFilterOnDesktop,
  };
};

/**
 * Merges global override with layout-specific override.
 * Layout-specific overrides take precedence over global ones.
 *
 * @param globalOverride - Global sticky override configuration
 * @param layoutSpecificOverride - Layout-specific sticky override configuration
 * @returns Merged override configuration
 */
export function mergeOverrides(
  globalOverride?: StickyOverrideConfig,
  layoutSpecificOverride?: StickyOverrideConfig
): StickyOverrideConfig | undefined {
  if (!globalOverride && !layoutSpecificOverride) {
    return undefined;
  }

  if (!globalOverride) {
    return layoutSpecificOverride;
  }

  if (!layoutSpecificOverride) {
    return globalOverride;
  }

  // Deep merge with layout-specific taking precedence
  return {
    elements: {
      ...globalOverride.elements,
      ...layoutSpecificOverride.elements,
    },
    styling: {
      ...globalOverride.styling,
      ...layoutSpecificOverride.styling,
    },
    behavior: {
      ...globalOverride.behavior,
      ...layoutSpecificOverride.behavior,
    },
    hooks: {
      ...globalOverride.hooks,
      ...layoutSpecificOverride.hooks,
    },
    debug: layoutSpecificOverride.debug ?? globalOverride.debug,
  };
}

// Helper function to get global and layout-specific sticky overrides
function getConfigOverrides(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
): {
  globalOverride: StickyOverrideConfig | undefined;
  layoutSpecificOverride: StickyOverrideConfig | undefined;
} {
  const config = window.boostWidgetIntegration?.config?.customization?.filter?.sticky;
  const globalOverride = config?.global;
  const layoutSpecificOverride = config?.[layoutType]?.[layoutStyle];
  return { globalOverride, layoutSpecificOverride };
}

/**
 * Generic helper function to get merged sticky configuration for any layout type.
 *
 * @param layoutType - The layout type (mobile, vertical, horizontal)
 * @param layoutStyle - The specific style within that layout
 * @returns Object containing merged configuration and individual configs for nested elements
 */
export function getStickyConfig(
  layoutType: 'mobile' | 'vertical' | 'horizontal',
  layoutStyle: string
) {
  // Use helper to get overrides
  const { globalOverride, layoutSpecificOverride } = getConfigOverrides(layoutType, layoutStyle);

  // Merge global override with layout-specific override (layout-specific takes precedence)
  const mergedOverride = mergeOverrides(globalOverride, layoutSpecificOverride);

  // Cast to extended type to access nested properties
  const extendedOverride = mergedOverride as ExtendedStickyOverrideConfig;

  return {
    merged: mergedOverride,
    // For horizontal functions, we might have nested configurations
    panel: extendedOverride?.panel || mergedOverride,
    content: extendedOverride?.body || mergedOverride,
    // For vertical functions
    toolbar: extendedOverride?.toolbar || mergedOverride,
  };
}

export const stickyMobile = async (context: Context) => {
  const { filterTreeMobileStyle } = getFilterSettings(context);

  // Get layout-specific override first
  const layoutSpecificOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.mobile?.[
      filterTreeMobileStyle
    ];

  // Get global override that applies to all layouts
  const globalOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.global;

  // Merge global override with layout-specific override (layout-specific takes precedence)
  const mergedOverride = mergeOverrides(globalOverride, layoutSpecificOverride);

  const init = initializeStickyMobile(context, mergedOverride);
  switch (filterTreeMobileStyle) {
    case 'style1':
      await init.loadStickyFilterTreeMobileExpand();
      break;
    default:
      await init.loadStickyFilterTreeMobile();
      break;
  }
};

export const stickyVertical = async (context: Context) => {
  const { filterTreeVerticalStyle } = getFilterSettings(context);

  // Get layout-specific override first
  const layoutSpecificOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.vertical?.[
      filterTreeVerticalStyle
    ];

  // Get global override that applies to all layouts
  const globalOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.global;

  // Merge global override with layout-specific override (layout-specific takes precedence)
  const mergedOverride = mergeOverrides(globalOverride, layoutSpecificOverride);

  const init = initializeStickyVertical(context, mergedOverride);

  switch (filterTreeVerticalStyle) {
    case 'style-default': {
      await init.loadStickyFilterTree();
      break;
    }
    case 'style-expand': {
      // For style-expand, we can have nested toolbar config
      const toolbarOverride =
        (mergedOverride as ExtendedStickyOverrideConfig)?.toolbar || mergedOverride;
      await init.loadStickyFilterTreeExpand(toolbarOverride);
      break;
    }
    default: {
      await init.loadStickyFilterTreeOffCanvas();
      break;
    }
  }
};

export const stickyHorizontal = async (context: Context) => {
  const { filterTreeHorizontalStyle } = getFilterSettings(context);

  // Get layout-specific override first
  const layoutSpecificOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.horizontal?.[
      filterTreeHorizontalStyle
    ];

  // Get global override that applies to all layouts
  const globalOverride =
    window.boostWidgetIntegration?.config?.customization?.filter?.sticky?.global;

  // Merge global override with layout-specific override (layout-specific takes precedence)
  const mergedOverride = mergeOverrides(globalOverride, layoutSpecificOverride);

  const init = initializeStickyHorizontal(context, mergedOverride);
  switch (filterTreeHorizontalStyle) {
    case 'style-expand': {
      // For style-expand, we can have nested toolbar config
      const toolbarOverride =
        (mergedOverride as ExtendedStickyOverrideConfig)?.toolbar || mergedOverride;
      await init.loadStickyFilterTreeHorizontalToolbar(toolbarOverride);
      break;
    }
    default: {
      await init.loadStickyFilterTreeHorizontal();
      break;
    }
  }
};

export * from './sticky-dynamic';
